'use client';

import { MouseEvent, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Session } from 'next-auth';
import BrandDefinition from '@/types/BrandDefinition';
import HeaderDictionary from '@/types/dictionaries/Header';
import BillingProfileDialog from '@/components/Header/components/BillingProfileDialog';
import AddressDictionary from '@/types/dictionaries/Address';
import AddressSelectionDictionary from '@/types/dictionaries/AddressSelection';
import UserAccountMenuButton from '@/components/Header/components/UserAccountMenuButton';
import BillingProfileMenuButton from '@/components/Header/components/BillingProfileMenuButton';
import { useAccountDataStore } from '@/utils/providers/AccountDataStoreProvider';
import { useShallow } from 'zustand/react/shallow';
import AccountMenu from '../AccountMenu';

interface AccountMenuButtonProps {
	sessionData: Session;
	brand: BrandDefinition;
	dict: { header: HeaderDictionary; addressCard: AddressDictionary; addressSelection: AddressSelectionDictionary };
	showBillingProfileInsteadOfUserAccount: boolean;
	showInvoicesInAccountMenuList: boolean;
}

export default function AccountMenuButton({
	sessionData,
	brand,
	dict,
	showBillingProfileInsteadOfUserAccount,
	showInvoicesInAccountMenuList,
}: Readonly<AccountMenuButtonProps>) {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [accountMobileMenuOpen, setAccountMobileMenuOpen] = useState(false);

	const theme = useTheme();
	const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));

	const openAccountMenu = (event: MouseEvent<HTMLElement>) => {
		isMobileOrTablet ? setAccountMobileMenuOpen(true) : setAnchorEl(event.currentTarget);
	};

	const closeAccountMenu = () => {
		setAnchorEl(null);
		setAccountMobileMenuOpen(false);
	};

	const forceUserToSelectBillingProfile =
		!sessionData.selectedBusinessUnit && sessionData.user.billingProfiles.length > 1;
	const [showBillingProfileDialog, setShowBillingProfileDialog] = useState(forceUserToSelectBillingProfile);

	const handleOpenBillingProfileDialog = () => {
		closeAccountMenu();
		setShowBillingProfileDialog(true);
	};

	const handleCloseBillingProfileDialog = () => setShowBillingProfileDialog(false);

	const { billingProfileName, billingProfileCity, billingProfileState, firstName, lastName } = useAccountDataStore(
		useShallow((state) => ({
			billingProfileName: state.selectedBillingProfile?.addressContact.businessUnitName,
			billingProfileCity: state.selectedBillingProfile?.addressData.city,
			billingProfileState: state.selectedBillingProfile?.addressData.state,
			firstName: state.firstName,
			lastName: state.lastName,
		})),
	);

	return (
		<>
			{showBillingProfileInsteadOfUserAccount ? (
				<BillingProfileMenuButton
					openAccountMenu={openAccountMenu}
					name={billingProfileName}
					city={billingProfileCity}
					state={billingProfileState}
				/>
			) : (
				<UserAccountMenuButton openAccountMenu={openAccountMenu} firstName={firstName} lastName={lastName} />
			)}
			<AccountMenu
				anchorEl={anchorEl}
				mobileMenuOpen={accountMobileMenuOpen}
				handleClose={closeAccountMenu}
				brand={brand}
				sessionData={sessionData}
				showBillingProfileMenuHeader={showBillingProfileInsteadOfUserAccount}
				dict={dict.header}
				onOpenBillingProfileDialog={handleOpenBillingProfileDialog}
				showInvoicesInAccountMenuList={showInvoicesInAccountMenuList}
			/>
			<BillingProfileDialog
				dict={dict}
				isOpen={showBillingProfileDialog}
				onClose={handleCloseBillingProfileDialog}
				forceUserToSelectBillingProfile={forceUserToSelectBillingProfile}
			/>
		</>
	);
}
